import styled from 'styled-components';
import camelCase from 'lodash/camelCase';
import { useTranslation } from 'react-i18next';
import { Box, FormContext, FormProvider, LocalCashTables, mainTheme, Tabs } from 'dash-ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Properties from 'containers/properties/Properties/Properties';
import { RoutesPath } from 'flow-components/routes/routes';
import { getCurrentTabFromSearchParam } from 'common/helpers';
import DeliveryImage from 'assets/images/png/Delivery.png';
import SearchingImage from 'assets/images/png/Searching.png';

import {
  DEFAULT_ORGANIZATION_TAB_KEY,
  OrganizationTabsKeys,
} from './OrganizationDetailsTabs.constants';
import { InternalSaleTeamForm, OrganizationUsers } from './components/';
import { useGetMarketAreasQuery, useGetUsStateByNameQuery, useGetUsStatesQuery } from 'gql/generated.graphql';
import { useContext, useMemo } from 'react';

const TabsBox = styled(Box)`
  border-top: 1px solid ${mainTheme.colors.n30};
  border-bottom: 1px solid ${mainTheme.colors.n30};
`;

function OrganizationDetailsTabs() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['tables']);
  const { t: tCommonLabels } = useTranslation('common', {
    keyPrefix: 'labels',
  });
  const currentTab = getCurrentTabFromSearchParam(
    location.search
  ) as OrganizationTabsKeys;
  const activeTab = currentTab || DEFAULT_ORGANIZATION_TAB_KEY;
  const { id: organizationId } = useParams();
  const { data: statesData } = useGetUsStatesQuery({
    variables: {
      filter: {
        withMarketArea: true,
      }
    }
  });
  const { formState } = useContext(FormContext);

  const statesOptions = useMemo(() => {
    const states = statesData?.getUSStates || [];
    return states.map(({ name }) => ({
      value: name,
      label: name,
    }));
  }, [statesData]);

  const { data: result } = useGetMarketAreasQuery({
    variables: {
      filter: {
        stateNames: formState.state && [formState.state],
      },
      pagination: {
        allowLimit: false,
      },
      sort: {
        fieldName: 'name',
        sortOrder: 1,
      },
    },
  });

  const { marketAreas } = useMemo(
    () => ({
      marketAreas: result?.getMarketAreas?.collection || [],
    }),
    [result]
  );

  const marketOptions = useMemo(() => {
    return marketAreas.map(({ _id, name }) => ({
      value: _id,
      label: name,
    }));
  }, [marketAreas]);

  return (
    <>
      <TabsBox w="100%">
        <Tabs
          variation="secondary"
          tabs={Object.values(OrganizationTabsKeys).map((key) => ({
            key,
            label: tCommonLabels(camelCase(key)),
          }))}
          activeTab={activeTab}
          onClick={(key) =>
            navigate(`${RoutesPath.Organizations}/${organizationId}?tab=${key}`)
          }
        />
      </TabsBox>
      <Box w="100%">
        {activeTab === OrganizationTabsKeys.PROPERTIES && (
          <Properties
            filter={{ organizationId: organizationId || '' }}
            tableProps={{
              hideColumns: ['organization', 'preview'],
              actionProps: {
                marketOptions,
                statesOptions,
                withStatusFilter: false,
                filterTableName: LocalCashTables.ORGANIZATIONS_PROPERTIES,
              },
              placeholder: t('tables:organizationProperties:placeholder') || '',
              placeholderImage: DeliveryImage,
              searchPlaceholder:
                t('tables:organizationProperties:placeholderSearch') || '',
              searchPlaceholderImage: SearchingImage,
            }}
            storageTableName={LocalCashTables.ORGANIZATIONS_PROPERTIES}
          />
        )}
        {activeTab === OrganizationTabsKeys.USERS && (
          <OrganizationUsers
            filter={{ organizationId: organizationId || '' }}
          />
        )}
        {activeTab === OrganizationTabsKeys.INTERNAL_SALE_TEAM && (
          <InternalSaleTeamForm organizationId={organizationId || ''} />
        )}
      </Box>
    </>
  );
}

export default OrganizationDetailsTabs;
